import { skipToken } from '@reduxjs/toolkit/query';
import { buttonStyle } from 'components/buttons/buttonStyle';
import CancelButton, { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import CircularLoader from 'components/CircularLoader';
import {
    useCreateDialogChip,
    useCreateDialogForm,
    useCreateDialogRequest,
    useCreateRoute,
} from 'components/create-dialog/CreateDialogContext';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import { mustNotExceed, numberIsGreaterThan } from 'components/forms/inputs/rhf-validators/validators';
import { WarningAmberOutlined } from 'components/icons';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
// TODO(react-doctor/no-flush-sync): cannot replace with `startTransition`. The flushSync
// calls below flip a Controller `rules.required` state immediately before `handleSubmit`
// fires, so the new rule must be visible to RHF's synchronous validation pass. Deferring
// via startTransition would let validation read the stale rule and block the draft save.
// See the comments around `activityTypeRequired` in useTimecardForm for the full rationale.
import { flushSync } from 'react-dom';
import { FormProvider } from 'react-hook-form';
import { useSelector } from 'store';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useAddInvoiceTimecardMutation, useAddMultiAuthorInvoiceTimecardsMutation } from 'store/api/invoice-details.ts';
import {
    useAddTimecardMutation,
    useFinishMultiAuthorTimecardsMutation,
    useFinishTimecardMutation,
    useSaveFinishedTimecardAsDraftMutation,
    useUpdateTimecardMutation,
} from 'store/api/timecards';
import { mapToExpensesCreateOnTimecardDto } from 'types/expenses';
import { TimecardDto, TimecardStatusType } from 'types/timecards';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { captureTimecardCreated, captureTimecardFinished, captureTimecardUpdated } from 'utils/timecardAnalytics';
import { useGoBack, useNavigateWithReturnState, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { useRejectedSubmitCapture } from 'utils/useRejectedSubmitCapture';
import { WHITE_OUTLINED_CLASS } from './buttons/whiteOutlinedClass';
import { AuthorFields } from './form-sections/AuthorFields';
import { CaseFields } from './form-sections/CaseFields';
import { DescriptionFields } from './form-sections/DescriptionFields';
import { ExpensesFields } from './form-sections/ExpensesFields';
import { PracticeAreaFields } from './form-sections/PracticeAreaFields';
import { FORM_ALERT_CLASS, TimeBillingFields } from './form-sections/TimeBillingFields';
import { TimecardFormProps, useTimecardForm } from './form-sections/useTimecardForm';
import { TimecardStatusLabel } from './labels/TimecardStatusLabel';
import { timecardFormToRequest } from './timecardFormMapping';
import { TimerLogsList } from './TimerLogsList';
import { FINISH_BLOCKING_MATTER_STATES } from './utils/timecardUtils';

type Props = Omit<TimecardFormProps, 'inPlace'> & {
    /** What the create dialog's header says a duplicate was copied from. */
    copyOf?: string;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    matter: 'timecards.timecard',
    matterAssignment: 'timecards.timecard',
    timecardActivityType: 'timecards.timecard',
    description: 'timecards.timecard',
    employee: 'timecards.timecard',
    timecardDate: 'timecards.timecard',
    isNonBillable: 'timecards.timecard',
    preserveNonBillableHours: 'timecards.timecard',
    totalTimeInHours: 'timecards.timecard',
    billedTimeInHours: 'timecards.timecard',
    remunerationCalcType: 'timecards.timecard',
    remunerationTimeInHours: 'timecards.timecard',
    multiplierTypeCode: 'timecards.timecard',
    practiceAreas: 'timecards.timecard',
    expenses: 'expenses.expenses',
};

/** The analytics key of a submit the form refuses, by the form's `type`. */
const REJECTED_FORM_KEYS = { add: 'timecard', edit: 'timecard-edit', finish: 'timecard-finish' } as const;

const TimecardPageForm = ({
    type = 'add',
    initialData,
    timecard,
    prefillWithCurrentUser,
    copyOf,
}: Props): JSX.Element => {
    useFocusedEditMode('timecards');
    const form = useTimecardForm({ type, initialData, timecard, prefillWithCurrentUser });
    const {
        methods,
        user,
        status,
        classifiers,
        currentMatter,
        isNewInvoiceTimecard,
        shouldSkipMatterQuery,
        refetchMatter,
        watchedEmployees,
        isMultiSelectMode,
        hasMultipleEmployees,
        isTimecardActivityTypeOptions,
        showAssignmentSelectInput,
        areAllAssignmentsDisabled,
        isZeroHoursAllowedOnDraftTc,
        setActivityTypeRequired,
        matterPrefilled,
    } = form;
    useCreateDialogForm(methods);
    const { control, register, reset } = methods;
    const handleSubmit = useRejectedSubmitCapture(methods.handleSubmit, REJECTED_FORM_KEYS[type]);
    const { invoiceId, matterId, id, contactId } = form.params;
    const userId = user?.id;

    const [addTimecard] = useAddTimecardMutation();
    const [finishTimecard] = useFinishTimecardMutation();
    const [finishMultiAuthorTimecards, { isLoading: isMultiAuthorLoading }] = useFinishMultiAuthorTimecardsMutation();
    const [updateTimecard] = useUpdateTimecardMutation();
    const [addInvoiceTimecard] = useAddInvoiceTimecardMutation();
    const [addMultiAuthorInvoiceTimecards] = useAddMultiAuthorInvoiceTimecardsMutation();
    const [saveFinishedTimecardAsDraft] = useSaveFinishedTimecardAsDraftMutation();

    const sectionErrors = useSectionErrors(form.errors, FIELD_TO_SECTION_MAP);
    const { buttonsDisabled } = useSelector((state) => state.buttons);

    // The case picker only offers active cases, but a draft's stored case may have been
    // transitioned since (e.g. Offer won → timecards moved to the new case, drafts left behind).
    const offersFinishAction =
        type === 'finish' ||
        (type === 'edit' && status === TimecardStatusType.DRAFT) ||
        (type === 'add' && !isNewInvoiceTimecard);
    const isFinishBlockedByCaseState =
        offersFinishAction &&
        !!currentMatter &&
        FINISH_BLOCKING_MATTER_STATES.includes(currentMatter.matterStateTypeCode);

    const navigateWithState = useNavigateWithReturnState();
    const { location } = useCreateRoute();
    const createDialog = useCreateDialogRequest();
    const returnTo = useReturnToLocation(matterId ? `/matter/${matterId}` : '/timecards');
    const goBack = useGoBack(returnTo);

    // The dialog's header says which case or contact the timecard was started for: on an invoice by the case's
    // number, as the invoice names its cases. The form holds a contact as an id only, so the dialog alone asks for
    // its name.
    const { data: parentContact } = useGetContactByIdQuery(createDialog && contactId ? contactId : skipToken);
    useCreateDialogChip({
        parent: invoiceId ? matterPrefilled?.caseNumber : (matterPrefilled?.fullName ?? parentContact?.name),
        copyOf,
    });

    /**
     * Leaves once a save went through, for where the form was opened from. The create dialog is on no page of
     * its own: it finishes with what was saved, the timecard or the list when one save made several, and the
     * invoice behind it is re-entered with `invoiceState`. A `draft` save finishes it as a draft.
     */
    const leaveAfterSave = (saved: { id: number }[], invoiceState?: unknown, draft = false) => {
        if (createDialog) {
            const target = saved.length === 1 ? `/timecards/${saved[0].id}` : '/timecards';
            if (draft) {
                createDialog.finish(target, { draft: true });
            } else if (invoiceState === undefined) {
                createDialog.finish(target);
            } else {
                createDialog.finish(target, { pageState: invoiceState });
            }
        } else if (invoiceState === undefined) {
            navigateWithState(returnTo);
        } else {
            const returnUrl = typeof returnTo === 'string' ? returnTo : returnTo.pathname || '';
            navigateWithState(returnUrl, { state: invoiceState });
        }
    };

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (
        datau: TimecardDto,
        action: 'createDraft' | 'update' | 'finish' | 'createInvoiceTimecard' | 'saveAsDraft'
    ): Promise<void> => {
        try {
            const data = timecardFormToRequest(datau);
            const savedDescription = {
                description: data.description,
                timecardLanguageCode: currentMatter?.timecardLanguageCode,
            };

            if (action === 'createDraft') {
                const employeeForDraft =
                    isMultiSelectMode && watchedEmployees?.length === 1 ? watchedEmployees[0] : data.employee;
                addTimecard({
                    ...data,
                    employee: employeeForDraft,
                    expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                })
                    .unwrap()
                    .then((saved) => {
                        void captureTimecardCreated({ via: 'form', kind: 'draft', ...savedDescription });
                        bypassNavigationBlockerRef.current = true;
                        if (matterId && !shouldSkipMatterQuery) refetchMatter();
                        reset({}, { keepValues: true });
                        leaveAfterSave([saved], undefined, true);
                    });
            } else if (action === 'finish' && hasMultipleEmployees && watchedEmployees) {
                finishMultiAuthorTimecards({
                    ...data,
                    employee: watchedEmployees[0],
                    employees: watchedEmployees,
                    expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                })
                    .unwrap()
                    .then((saved) => {
                        // Created even from an existing draft: the backend writes a new timecard per author and
                        // leaves the draft as it was.
                        void captureTimecardCreated({
                            via: 'form',
                            kind: 'multi_author',
                            timecardCount: saved.length,
                            ...savedDescription,
                        });
                        bypassNavigationBlockerRef.current = true;
                        if (matterId && !shouldSkipMatterQuery) refetchMatter();
                        reset({}, { keepValues: true });
                        leaveAfterSave(saved);
                    });
            } else if (action === 'finish') {
                const employeeValue =
                    isMultiSelectMode && watchedEmployees?.length === 1 ? watchedEmployees[0] : data.employee;
                finishTimecard({
                    ...data,
                    employee: employeeValue,
                    expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                    userId: user?.id,
                    tenantId: user?.activeTenantId,
                })
                    .unwrap()
                    .then((saved) => {
                        if (datau.id) {
                            void captureTimecardFinished({ timerLogs: timecard?.timerLogs, ...savedDescription });
                        } else {
                            void captureTimecardCreated({ via: 'form', kind: 'finished', ...savedDescription });
                        }
                        bypassNavigationBlockerRef.current = true;
                        if (matterId && !shouldSkipMatterQuery) refetchMatter();
                        reset({}, { keepValues: true });
                        leaveAfterSave([saved]);
                    });
            } else if (action === 'update') {
                const employeeForUpdate =
                    isMultiSelectMode && watchedEmployees?.length === 1 ? watchedEmployees[0] : data.employee;
                updateTimecard({
                    ...data,
                    employee: employeeForUpdate,
                    expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                })
                    .unwrap()
                    .then((saved) => {
                        void captureTimecardUpdated({
                            via: 'form',
                            descriptionChanged: data.description !== timecard?.description,
                            ...savedDescription,
                        });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveAfterSave([saved]);
                    });
            } else if (action === 'saveAsDraft') {
                saveFinishedTimecardAsDraft({
                    ...data,
                    expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                })
                    .unwrap()
                    .then((saved) => {
                        void captureTimecardUpdated({
                            via: 'form',
                            descriptionChanged: data.description !== timecard?.description,
                            ...savedDescription,
                        });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveAfterSave([saved]);
                    });
            } else if (
                action === 'createInvoiceTimecard' &&
                hasMultipleEmployees &&
                watchedEmployees &&
                invoiceId &&
                matterId
            ) {
                addMultiAuthorInvoiceTimecards({
                    invoiceId: Number(invoiceId),
                    matterId: Number(matterId),
                    dto: {
                        ...data,
                        employee: watchedEmployees[0],
                        employees: watchedEmployees,
                        expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                    },
                })
                    .unwrap()
                    .then((newTimecards) => {
                        void captureTimecardCreated({
                            via: 'form',
                            kind: 'invoice',
                            timecardCount: newTimecards.length,
                            ...savedDescription,
                        });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveAfterSave(
                            newTimecards.map((tc) => tc.timecard),
                            { ...location.state, newTimecardIds: newTimecards.map((tc) => tc.timecard.id) }
                        );
                    });
            } else if (action === 'createInvoiceTimecard' && invoiceId && matterId) {
                const employeeForInvoice =
                    isMultiSelectMode && watchedEmployees?.length === 1 ? watchedEmployees[0] : data.employee;
                addInvoiceTimecard({
                    invoiceId: Number(invoiceId),
                    matterId: Number(matterId),
                    dto: {
                        ...data,
                        employee: employeeForInvoice,
                        expenses: mapToExpensesCreateOnTimecardDto(data?.expenses || []),
                    },
                })
                    .unwrap()
                    .then((newTimecard) => {
                        void captureTimecardCreated({ via: 'form', kind: 'invoice', ...savedDescription });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        // The invoice highlights the timecards whose ids it finds in its state.
                        leaveAfterSave([newTimecard.timecard], {
                            ...location.state,
                            newTimecardIds: [newTimecard.timecard.id],
                        });
                    });
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    if (form.isLoading) {
        return <Loader />;
    }

    const getFormTitleId = () => {
        switch (type) {
            case 'finish':
                return 'timecards.finish-timecard';
            case 'edit':
                return 'timecards.edit-timecard';
            default:
                return 'timecards.add-timecard';
        }
    };

    const submitFinishTimecard = async () => {
        // register temporary validations when finishing timecard
        register('totalTimeInHours', {
            validate: {
                greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
            },
        });
        if (isTimecardActivityTypeOptions) {
            register('timecardActivityType', { required: { value: true, message: t('validation.required-field') } });
        }

        if (showAssignmentSelectInput && !areAllAssignmentsDisabled) {
            register('matterAssignment', { required: { value: true, message: t('validation.required-field') } });
        }

        await handleSubmit((data) => onSubmit(data, 'finish'))();

        // unregister temporary validations
        register('matterAssignment', { required: false });
        register('timecardActivityType', { required: false });
        register('totalTimeInHours', {
            validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
        });
    };

    const submitSaveAsDraft = async () => {
        if (!isZeroHoursAllowedOnDraftTc) {
            register('totalTimeInHours', {
                validate: {
                    greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                    notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
                },
            });
        }
        // The activity-type field defaults to required (see `activityTypeRequired`).
        // Draft saves are exempt on the BE, so suppress the rule for this submit only.
        // `flushSync` is required: the SelectInput is Controller-based, so its `rules`
        // are set during render — we must let React commit the new `required={false}`
        // before `handleSubmit` reads the field's rules, otherwise validation still sees
        // `required: true` and rejects the empty value.
        flushSync(() => setActivityTypeRequired(false));
        try {
            await handleSubmit((data) => onSubmit(data, 'saveAsDraft'))();
        } finally {
            setActivityTypeRequired(true);
        }
        if (!isZeroHoursAllowedOnDraftTc) {
            register('totalTimeInHours', {
                validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
            });
        }
    };

    const submitCreateTimecard = async () => {
        if (!isZeroHoursAllowedOnDraftTc) {
            register('totalTimeInHours', {
                validate: {
                    greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                    notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
                },
            });
        }

        // Creates a DRAFT timecard — BE does not require activity type for DRAFT, so
        // suppress the FE rule for this submit. See `activityTypeRequired` declaration
        // for the full rationale and why `flushSync` is necessary.
        flushSync(() => setActivityTypeRequired(false));
        try {
            await handleSubmit((data) => onSubmit(data, 'createDraft'))();
        } finally {
            setActivityTypeRequired(true);
        }

        if (!isZeroHoursAllowedOnDraftTc) {
            register('totalTimeInHours', {
                validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
            });
        }
    };

    const submitCreateInvoiceTimecard = async () => {
        // register temporary validations when creating DRAFT_INVOICE timecard
        register('totalTimeInHours', {
            validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
        });
        if (isTimecardActivityTypeOptions) {
            register('timecardActivityType', { required: { value: true, message: t('validation.required-field') } });
        }

        await handleSubmit((data) => onSubmit(data, 'createInvoiceTimecard'))();

        // unregister temporary validations
        register('matterAssignment', { required: false });
        register('timecardActivityType', { required: false });
        register('totalTimeInHours', {
            validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
        });
    };

    const submitUpdateTimecard = async () => {
        if (
            status === TimecardStatusType.FINISHED ||
            status === TimecardStatusType.DONE ||
            status === TimecardStatusType.DRAFT_INVOICE
        ) {
            // register temporary validations when updating FINISHED, DONE or DRAFT_INVOICE timecard
            register('totalTimeInHours', {
                validate: {
                    greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                    notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
                },
            });
            if (isTimecardActivityTypeOptions) {
                register('timecardActivityType', {
                    required: { value: true, message: t('validation.required-field') },
                });
            }
            if (showAssignmentSelectInput && !areAllAssignmentsDisabled) {
                register('matterAssignment', { required: { value: true, message: t('validation.required-field') } });
            }
        } else if (status === TimecardStatusType.DRAFT && !isZeroHoursAllowedOnDraftTc) {
            register('totalTimeInHours', {
                validate: {
                    greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                    notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
                },
            });
        }
        // Updating a DRAFT keeps the timecard in DRAFT, which the BE does not gate on
        // activity type; suppress the FE rule for this submit only. See
        // `activityTypeRequired` declaration for full rationale, including why
        // `flushSync` is required to make the rule change visible to validation.
        const isDraftUpdate = status === TimecardStatusType.DRAFT;
        if (isDraftUpdate) {
            flushSync(() => setActivityTypeRequired(false));
        }
        try {
            await handleSubmit((data) => onSubmit(data, 'update'))();
        } finally {
            if (isDraftUpdate) {
                setActivityTypeRequired(true);
            }
        }

        // unregister temporary validations
        register('matterAssignment', { required: false });
        register('timecardActivityType', { required: false });
        register('totalTimeInHours', {
            validate: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
        });
    };

    const createPermissions = ['CREATE.TIMECARDS'];

    /** A draft action a multi-author timecard cannot take says why on hover. */
    const withMultiAuthorTooltip = (key: string, button: JSX.Element) =>
        hasMultipleEmployees ? (
            <Tooltip key={key} content={t('timecards.multi-author-draft-not-supported')}>
                <span>{button}</span>
            </Tooltip>
        ) : (
            <span key={key}>{button}</span>
        );

    const getFormHeader = () => {
        // The create dialog's footer knows a form's Cancel and its main action by their components: the dialog
        // gets `CancelButton`, and Finish is a `SaveButton`, which on a page is the plain contained button.
        const actions: JSX.Element[] = [
            createDialog ? (
                <CancelButton key="cancel" onClick={goBack} />
            ) : (
                <Button
                    key="cancel"
                    {...buttonStyle({ variant: 'outlined', size: 'small' }, WHITE_OUTLINED_CLASS)}
                    onClick={goBack}
                >
                    {t('cancel')}
                </Button>
            ),
        ];
        if (type === 'edit') {
            if (status === TimecardStatusType.DRAFT) {
                actions.push(
                    withMultiAuthorTooltip(
                        'updateTimecard',
                        <PermissionButton
                            hasAuthority={timecard?.isEditable}
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                                void submitUpdateTimecard();
                            }}
                            disabled={buttonsDisabled || hasMultipleEmployees}
                        >
                            {t('timecards.save-as-draft')}
                        </PermissionButton>
                    ),
                    <PermissionButton
                        key="editFinish"
                        hasAuthority={timecard?.isEditable}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => {
                            void submitFinishTimecard();
                        }}
                        disabled={buttonsDisabled || isFinishBlockedByCaseState}
                    >
                        {t('timecards.finish')}
                    </PermissionButton>
                );
            } else {
                const isCreator = timecard?.createdByEmployee?.id === userId;
                if (status === TimecardStatusType.FINISHED && isCreator) {
                    actions.push(
                        <PermissionButton
                            key="saveAsDraft"
                            hasAuthority={timecard?.isEditable}
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                                void submitSaveAsDraft();
                            }}
                            disabled={buttonsDisabled}
                        >
                            {t('timecards.save-as-draft')}
                        </PermissionButton>
                    );
                }
                actions.push(
                    <PermissionButton
                        key="save"
                        hasAuthority={timecard?.isEditable}
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            void submitUpdateTimecard();
                        }}
                        disabled={buttonsDisabled}
                    >
                        {t('save')}
                    </PermissionButton>
                );
            }
        } else if (type === 'finish') {
            if (id === undefined) {
                actions.push(
                    withMultiAuthorTooltip(
                        'createTimecard',
                        <PermissionButton
                            permissions={createPermissions}
                            variant="outlined"
                            color="secondary"
                            className={createDialog ? CREATE_DIALOG_ACTION_CLASS : undefined}
                            onClick={() => {
                                void submitCreateTimecard();
                            }}
                            disabled={buttonsDisabled || hasMultipleEmployees}
                        >
                            {t('timecards.save-as-draft')}
                        </PermissionButton>
                    )
                );
            } else {
                actions.push(
                    withMultiAuthorTooltip(
                        'finish_updateTimecard',
                        <PermissionButton
                            hasAuthority={timecard?.isEditable}
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                                void submitUpdateTimecard();
                            }}
                            disabled={buttonsDisabled || hasMultipleEmployees}
                        >
                            {t('timecards.save-as-draft')}
                        </PermissionButton>
                    )
                );
            }
            actions.push(
                <SaveButton
                    key="finishTimecard"
                    permissions={createPermissions}
                    hasAuthority={timecard?.isEditable}
                    onClick={() => {
                        void submitFinishTimecard();
                    }}
                    disabled={buttonsDisabled || isFinishBlockedByCaseState}
                >
                    {t('timecards.finish')}
                </SaveButton>
            );
        } else if (type === 'add' && isNewInvoiceTimecard) {
            const createInvoiceTimecard = () => {
                void submitCreateInvoiceTimecard();
            };
            actions.push(
                // The form's one action here, so the dialog's main one; the page draws it outlined.
                createDialog ? (
                    <SaveButton
                        key="addCreateTimecard"
                        permissions={createPermissions}
                        onClick={createInvoiceTimecard}
                        disabled={buttonsDisabled}
                    >
                        {t('timecards.create')}
                    </SaveButton>
                ) : (
                    <PermissionButton
                        key="addCreateTimecard"
                        permissions={createPermissions}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={createInvoiceTimecard}
                        disabled={buttonsDisabled}
                    >
                        {t('timecards.create')}
                    </PermissionButton>
                )
            );
        } else if (type === 'add') {
            actions.push(
                withMultiAuthorTooltip(
                    'addCreateTimecard',
                    <PermissionButton
                        permissions={createPermissions}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        className={createDialog ? CREATE_DIALOG_ACTION_CLASS : undefined}
                        onClick={() => {
                            void submitCreateTimecard();
                        }}
                        disabled={buttonsDisabled || hasMultipleEmployees}
                    >
                        {t('timecards.save-as-draft')}
                    </PermissionButton>
                ),
                <SaveButton
                    key="addFinishTimecard"
                    permissions={createPermissions}
                    onClick={() => {
                        void submitFinishTimecard();
                    }}
                    disabled={buttonsDisabled || isFinishBlockedByCaseState}
                >
                    {t('timecards.finish')}
                </SaveButton>
            );
        }

        return (
            <ActionHeader
                title={t(getFormTitleId())}
                titleSuffixComponent={
                    <TimecardStatusLabel statusCode={status} classifier={classifiers['TIMECARD_STATUS_TYPE']} />
                }
                actions={actions}
                onBack={goBack}
            />
        );
    };

    return (
        <FormProvider {...methods}>
            {/* Blocks the whole window, open modals included, while a multi-author timecard is finished;
                it stays mounted so it can fade out. */}
            <div
                className={cn(
                    'new:bg-(--blocking-scrim) bg-grey-900/50 fixed inset-0 z-(--z-blocking-loader) transition-[opacity,visibility] duration-225',
                    isMultiAuthorLoading ? 'visible opacity-100' : 'invisible opacity-0'
                )}
            >
                <CircularLoader className="[&>span]:border-fg [&>span]:border-t-transparent" />
            </div>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        {isFinishBlockedByCaseState && (
                            <Alert className={cn(FORM_ALERT_CLASS, 'border-warning bg-warning/25 mx-6 mt-4 w-auto')}>
                                <span className="flex w-6 shrink-0 justify-center">
                                    <WarningAmberOutlined className="text-warning size-4" />
                                </span>
                                <div className="min-w-0">
                                    {t('timecards.form.finish-blocked-by-case-state', {
                                        status: getClassifierText(
                                            classifiers['MATTER_STATE_TYPE'],
                                            currentMatter?.matterStateTypeCode
                                        ),
                                    })}
                                </div>
                            </Alert>
                        )}
                        <SectionLayout sectionErrors={sectionErrors}>
                            <Section title="timecards.timecard" collapsible>
                                <CaseFields form={form} />
                                <DescriptionFields form={form} />
                                <AuthorFields form={form} />
                                <TimeBillingFields form={form} />
                                <PracticeAreaFields form={form} />
                            </Section>
                            {timecard?.timerLogs && timecard.timerLogs.length > 0 ? (
                                <Section title="timecards.timer-logs" collapsible defaultCollapsed>
                                    <TimerLogsList timerLogs={timecard.timerLogs} />
                                </Section>
                            ) : null}
                            {isNewInvoiceTimecard ? null : (
                                <Section title="expenses.expenses" collapsible>
                                    <ExpensesFields form={form} />
                                </Section>
                            )}
                        </SectionLayout>
                        <ConfirmNavigation control={control} bypassRef={bypassNavigationBlockerRef} />
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default TimecardPageForm;
